import { motion } from "framer-motion";
import { useI18n } from "@/context/I18nContext";

const founders = [
  {
    key: "rajesh",
    initials: "RJ",
    name: "Rajesh Jain",
    nameHi: "राजेश जैन",
    title: "Founder · Quality & Sourcing",
    titleHi: "संस्थापक · गुणवत्ता व स्रोत",
    text: "Ensuring every drop of GIR Gaumata milk meets the highest purity standards. Rajesh oversees quality control, cow welfare and authentic sourcing at Matrubhumi Gauras Bhandar.",
    textHi: "हर बूंद GIR गौमाता दूध की शुद्धता सुनिश्चित करना — गुणवत्ता नियंत्रण, गोशाला की देखरेख एवं प्रामाणिक स्रोत की ज़िम्मेदारी राजेश जी निभाते हैं।",
    image: "/founder-rajesh.png",
  },
  {
    key: "darshan",
    initials: "DB",
    name: "Darshan Bhardwaj",
    nameHi: "दर्शन भारद्वाज",
    title: "Founder · Delivery & Operations",
    titleHi: "संस्थापक · डिलीवरी व संचालन",
    text: "Darshan ensures every family in Nerul, Kharghar, Vashi and all of Navi Mumbai receives fresh A2 GIR milk from 6 AM onwards, every day.",
    textHi: "दर्शन जी यह सुनिश्चित करते हैं कि संपूर्ण नवी मुंबई के प्रत्येक परिवार को प्रातः 6 बजे से ताज़ा A2 GIR दूध प्राप्त हो — प्रतिदिन।",
    image: "https://customer-assets.emergentagent.com/job_pure-milk-navi/artifacts/i4u2cdsl_darshan%20bhardwaj.jpeg",
  },
];

export default function Founders() {
  const { lang, t } = useI18n();

  return (
    <section id="founders" className="dd-section bg-dd-ivory" data-testid="founders-section">
      <div className="dd-container">
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <div className="dd-label text-dd-gold">{t.founders.eyebrow}</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-dd-ink mt-3">{t.founders.title}</h2>
          <div className="dd-ornament my-5"><span className="text-lg">✦</span></div>
          <p className="text-dd-ink-muted">{t.founders.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
          {founders.map((f, idx) => (
            <motion.article
              key={f.key}
              initial={{ opacity: 0, x: idx === 0 ? -40 : 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className="bg-white border border-dd-line rounded-2xl overflow-hidden group shadow-sm hover:shadow-xl hover:border-dd-gold transition-all"
              data-testid={`founder-${f.key}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={f.image} alt={f.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-dd-ink/50 via-transparent to-transparent" />
                <div className="absolute top-5 left-5 w-14 h-14 rounded-full bg-gradient-to-br from-dd-gold to-[#9A7428] text-white flex items-center justify-center font-serif text-xl shadow-lg">
                  {f.initials}
                </div>
                <div className="absolute bottom-5 left-5 inline-flex items-center gap-2 bg-white/95 backdrop-blur text-dd-ink px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-dd-green" /> Founder
                </div>
              </div>
              <div className="p-7">
                <h3 className="font-serif text-3xl text-dd-ink">{lang === "hi" ? f.nameHi : f.name}</h3>
                <div className="dd-label text-dd-gold mt-1">{lang === "hi" ? f.titleHi : f.title}</div>
                <p className="mt-4 text-dd-ink-muted text-[0.95rem] leading-relaxed">{lang === "hi" ? f.textHi : f.text}</p>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Why we started */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          className="mt-14 md:mt-20 grid lg:grid-cols-3 gap-8 items-center"
        >
          <div className="lg:col-span-2 relative bg-white border border-dd-line rounded-2xl p-8 md:p-10 overflow-hidden">
            <div className="absolute top-0 left-0 w-24 h-24 opacity-10" style={{ background: "radial-gradient(circle, #B8893B 0%, transparent 70%)" }} />
            <div className="dd-label text-dd-gold">{t.founders.story_title}</div>
            <blockquote className="mt-4 font-serif italic text-2xl md:text-3xl text-dd-ink leading-snug border-l-2 border-dd-gold pl-5">
              {t.founders.story}
            </blockquote>
            <p className="mt-5 text-dd-ink-muted leading-relaxed">{t.founders.story_p2}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { n: "2,300+", l: t.founders.stats.fam },
              { n: "2+", l: t.founders.stats.yrs },
              { n: "4.9", l: t.founders.stats.rating },
              { n: "99%", l: t.founders.stats.ret },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-dd-line rounded-xl p-5 text-center shadow-md">
                <div className="font-serif text-4xl text-[#8F6E28] font-semibold">{s.n}</div>
                <div className="dd-label text-dd-ink-muted mt-1 text-[0.64rem]">{s.l}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
